/**
 * LexiaIntroModal — first-run welcome sequence.
 *
 * Elegant modal with a big Lexia portrait, synchronized narration in French
 * (both text on screen AND audio via /api/poc/tts using the user's chosen
 * voice), then two CTAs: "Parler à Lexia maintenant" (opens the voice widget
 * in general mode) or "Plus tard" (closes and persists the flag).
 *
 * The flag `lexia_intro_seen` is persisted server-side via
 * POST /api/profile/lexia-intro/seen so the modal never reappears
 * automatically. It can be replayed from settings.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { X, Mic, Play, Pause, Loader2, VolumeX } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useLexiaVoice } from "@/contexts/LexiaVoiceContext";
import avatar512Webp from "@/assets/lexia/avatar-512.webp";
import avatar512Jpg from "@/assets/lexia/avatar-512.jpg";

const SCRIPT = [
    "Bonjour, moi c'est Lexia.",
    "Je suis votre coach vocale au fil de votre apprentissage.",
    "Chaque jour, je vous propose une leçon courte, des révisions calibrées, et une petite question à l'oral — deux minutes suffisent.",
    "Vous pouvez me parler à tout moment : cliquez sur ma bulle en bas à droite. Demandez-moi de traduire, de corriger une phrase, d'ouvrir vos révisions, ou juste de discuter.",
    // Uses non-breaking space (\u00A0) so the "?" doesn't wrap alone. No HTML.
    "On avance à votre rythme, un mot à la fois. On y va\u00A0?",
];

const FULL_TEXT_FOR_TTS =
    "Bonjour, moi c'est Lexia. Je suis votre coach vocale au fil de votre apprentissage. " +
    "Chaque jour, je vous propose une leçon courte, des révisions calibrées, et une petite question à l'oral — deux minutes suffisent. " +
    "Vous pouvez me parler à tout moment : cliquez sur ma bulle en bas à droite. Demandez-moi de traduire, de corriger une phrase, d'ouvrir vos révisions, ou juste de discuter. " +
    "On avance à votre rythme, un mot à la fois. On y va ?";

// Approx. durations (seconds) for the synchronized reveal without waiting on
// audio metadata — kept slightly ahead of real speech so the text is always
// visible before it is heard.
const SEGMENT_DURATIONS_S = [2.2, 3.4, 6.8, 8.8, 3.4];

export default function LexiaIntroModal({ voice = "shimmer", onClose }) {
    const [step, setStep] = useState(0);
    const [ttsState, setTtsState] = useState("loading"); // loading | playing | paused | ended | error | unavailable
    const [muted, setMuted] = useState(false);
    const audioRef = useRef(null);
    const timerRef = useRef(null);
    // Guard against React StrictMode double-invocation of useEffect
    // (would otherwise issue two TTS calls per mount).
    const bootedRef = useRef(false);
    const { openInMode } = useLexiaVoice();

    // Track visibility of each line for the reveal.
    const revealed = useMemo(() => SCRIPT.map((_, i) => i <= step), [step]);

    async function markSeen() {
        try {
            await api.post("/profile/lexia-intro/seen");
        } catch {
            /* silent — dashboard fetch will refresh soon anyway */
        }
    }

    async function close(after) {
        if (audioRef.current) {
            try { audioRef.current.pause(); } catch { /* noop */ }
        }
        if (timerRef.current) clearInterval(timerRef.current);
        await markSeen();
        onClose?.();
        if (after === "talk") {
            // Give the modal a beat to unmount before the widget opens so both
            // don't animate simultaneously.
            setTimeout(() => openInMode("general"), 200);
        }
    }

    // Load TTS + progress steps.
    useEffect(() => {
        if (bootedRef.current) return; // ignore StrictMode double-invoke
        bootedRef.current = true;
        let cancelled = false;

        async function boot() {
            let audio = null;
            let url = null;
            try {
                const { data } = await api.post("/poc/tts", {
                    text: FULL_TEXT_FOR_TTS,
                    language: "français",
                    voice,
                    speed: "natural",
                });
                if (cancelled) return;
                if (data?.audio_base64) {
                    const bin = atob(data.audio_base64);
                    const bytes = new Uint8Array(bin.length);
                    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
                    const blob = new Blob([bytes], { type: data.mime_type || "audio/mpeg" });
                    url = URL.createObjectURL(blob);
                    audio = new Audio(url);
                    audio.preload = "auto";
                    audioRef.current = audio;
                    audio.addEventListener("play", () => setTtsState("playing"));
                    audio.addEventListener("pause", () => setTtsState((s) => s === "ended" ? s : "paused"));
                    audio.addEventListener("ended", () => setTtsState("ended"));
                    audio.addEventListener("error", () => setTtsState("error"));
                }
            } catch {
                if (!cancelled) setTtsState("unavailable");
            }

            if (cancelled) {
                if (url) URL.revokeObjectURL(url);
                return;
            }

            // Kick off text reveal on a fixed schedule — decoupled from audio
            // so the sequence still tells its story even if TTS is unavailable.
            let elapsed = 0;
            timerRef.current = setInterval(() => {
                elapsed += 0.5;
                setStep((prev) => {
                    let cumulative = 0;
                    for (let i = 0; i < SEGMENT_DURATIONS_S.length; i++) {
                        cumulative += SEGMENT_DURATIONS_S[i];
                        if (elapsed < cumulative) return i;
                    }
                    return SCRIPT.length - 1;
                });
            }, 500);

            if (audio) {
                try {
                    await audio.play();
                } catch {
                    // Autoplay was blocked — user has a Play button to trigger.
                    setTtsState("paused");
                }
            }
        }

        boot();
        return () => {
            cancelled = true;
            if (timerRef.current) clearInterval(timerRef.current);
            if (audioRef.current) {
                try { audioRef.current.pause(); } catch { /* noop */ }
            }
        };
    }, [voice]);

    function togglePlay() {
        const a = audioRef.current;
        if (!a) return;
        if (a.paused || a.ended) {
            a.currentTime = a.ended ? 0 : a.currentTime;
            a.play().catch(() => toast.info("Cliquez à nouveau pour lancer l'audio."));
        } else {
            a.pause();
        }
    }

    function toggleMute() {
        const a = audioRef.current;
        setMuted((m) => {
            const next = !m;
            if (a) a.muted = next;
            return next;
        });
    }

    // A11y: Escape closes the modal (same effect as clicking "Plus tard").
    useEffect(() => {
        function onKey(e) {
            if (e.key === "Escape") close();
        }
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, []);

    return (
        <div
            className="fixed inset-0 z-[80] flex items-center justify-center p-4 sm:p-6 animate-in fade-in duration-200"
            role="dialog"
            aria-modal="true"
            aria-labelledby="lexia-intro-title"
            data-testid="lexia-intro-modal"
        >
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-ink/60 backdrop-blur-sm"
                onClick={() => close()}
                aria-hidden="true"
            />

            {/* Modal card */}
            <div className="relative w-full max-w-lg bg-paper border border-lexiBorder rounded-3xl shadow-2xl shadow-black/30 overflow-hidden animate-in zoom-in-95 duration-300">
                <button
                    onClick={() => close()}
                    data-testid="lexia-intro-close"
                    className="absolute top-3 right-3 z-10 rounded-full bg-white/90 hover:bg-white border border-lexiBorder w-8 h-8 inline-flex items-center justify-center text-inkSoft hover:text-ink"
                    aria-label="Fermer la présentation"
                >
                    <X className="w-4 h-4" />
                </button>

                {/* Portrait band */}
                <div className="relative bg-terracotta/12 pt-8 pb-4 flex flex-col items-center">
                    <div className="relative">
                        <picture>
                            <source srcSet={avatar512Webp} type="image/webp" />
                            <img
                                src={avatar512Jpg}
                                alt="Lexia, votre coach linguistique"
                                aria-label="Lexia, votre coach linguistique"
                                className="w-28 h-28 rounded-full object-cover ring-4 ring-white ring-offset-2 ring-offset-terracotta/20"
                                loading="eager"
                                decoding="async"
                            />
                        </picture>
                        <span
                            className={`absolute -bottom-1 -right-1 w-8 h-8 rounded-full inline-flex items-center justify-center border-2 border-white text-white ${ttsState === "playing" ? "bg-emerald animate-pulse" : "bg-terracotta"}`}
                            aria-hidden="true"
                        >
                            <Mic className="w-4 h-4" />
                        </span>
                    </div>
                    <p className="mt-4 text-[11px] tracking-[0.28em] uppercase text-terracotta font-semibold">
                        Votre coach vocale
                    </p>
                    <h2
                        id="lexia-intro-title"
                        className="font-serif text-2xl md:text-3xl text-ink mt-1"
                    >
                        Ravie de vous rencontrer.
                    </h2>
                </div>

                {/* Script */}
                <div className="p-6 md:p-7">
                    <ul className="space-y-3 text-sm md:text-[15px] text-ink leading-relaxed" data-testid="lexia-intro-script">
                        {SCRIPT.map((line, i) => (
                            <li
                                key={i}
                                className={`transition-opacity duration-500 ${revealed[i] ? "opacity-100" : "opacity-25"}`}
                                data-testid={`lexia-intro-line-${i}`}
                            >
                                {line}
                            </li>
                        ))}
                    </ul>

                    {/* Audio controls */}
                    {(ttsState === "playing" || ttsState === "paused" || ttsState === "ended" || ttsState === "loading") && (
                        <div className="mt-5 flex items-center gap-2" data-testid="lexia-intro-audio-controls">
                            <button
                                onClick={togglePlay}
                                disabled={ttsState === "loading" || !audioRef.current}
                                data-testid="lexia-intro-audio-toggle"
                                className="inline-flex items-center gap-1.5 rounded-full border border-lexiBorder bg-white hover:bg-paperAlt px-3 py-1.5 text-xs text-ink disabled:opacity-50 transition-colors"
                            >
                                {ttsState === "loading" ? (
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                ) : ttsState === "playing" ? (
                                    <Pause className="w-3.5 h-3.5" />
                                ) : (
                                    <Play className="w-3.5 h-3.5" />
                                )}
                                {ttsState === "loading" ? "Chargement…"
                                    : ttsState === "playing" ? "Pause"
                                        : ttsState === "ended" ? "Réécouter"
                                            : "Écouter"}
                            </button>
                            {audioRef.current && (
                                <button
                                    onClick={toggleMute}
                                    data-testid="lexia-intro-audio-mute"
                                    className="inline-flex items-center gap-1.5 rounded-full border border-lexiBorder bg-white hover:bg-paperAlt px-3 py-1.5 text-xs text-inkSoft transition-colors"
                                >
                                    <VolumeX className="w-3.5 h-3.5" />
                                    {muted ? "Rétablir le son" : "Couper le son"}
                                </button>
                            )}
                            <span className="text-[10px] text-inkSoft italic ml-auto">
                                {ttsState === "loading" ? "Voix en cours de chargement…"
                                    : ttsState === "playing" ? "Lexia parle…"
                                        : ttsState === "ended" ? "Terminé."
                                            : "En pause."}
                            </span>
                        </div>
                    )}
                    {ttsState === "unavailable" && (
                        <p className="mt-5 text-[11px] text-inkSoft italic">
                            L&apos;audio n&apos;est pas disponible pour le moment — lisez tranquillement, cela ira aussi bien.
                        </p>
                    )}

                    {/* Actions */}
                    <div className="mt-6 flex flex-wrap gap-2">
                        <button
                            onClick={() => close("talk")}
                            data-testid="lexia-intro-talk"
                            className="flex-1 min-w-[220px] inline-flex items-center justify-center gap-2 bg-terracotta hover:bg-terracottaDark text-white rounded-full px-4 py-2.5 text-sm font-medium transition-colors"
                        >
                            <Mic className="w-4 h-4" /> Parler à Lexia maintenant
                        </button>
                        <button
                            onClick={() => close()}
                            data-testid="lexia-intro-later"
                            className="inline-flex items-center justify-center bg-paperAlt border border-lexiBorder hover:bg-white text-ink rounded-full px-4 py-2.5 text-sm transition-colors"
                        >
                            Plus tard
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
